<template>
    <md-layout
        md-column
        v-if="bookmarkNavOpened"
        class="bookmarkNav"
        :class="{ 'bookmarkNav--resizing': resizing }"
        :style="navStyle">
        <md-toolbar class="bookmarkTopBar">
            <h3>Navigation</h3>
            <md-button class="closeBookmark md-fab md-clean md-mini" @click="toggleBookmarkNav">
                <md-icon>close</md-icon>
            </md-button>
        </md-toolbar>
        <md-tabs ref="navTabs" class="bookmark-nav-tabs" md-fixed @change="onTabChange">
            <md-tab id="structure" md-label="Structure" :md-active="currentTab === 'structure'">
                <p v-if="!navMarkers.length" class="bookmark-empty-hint">No act, scene, or song structure marks yet.</p>
                <md-list class="bookmarks-list marker-nav-list" v-else>
                    <marker-nav-item
                        v-for="marker in navMarkers"
                        :key="marker.markerId || marker.id"
                        :marker="marker"
                        :currentPage="currentPage"
                        @gotoPage="gotoPage"
                    />
                </md-list>
                <div class="structure-visibility">
                    <md-switch
                        v-model="hideMarksLocal"
                        :disabled="!navMarkers.length"
                        class="md-primary structure-visibility__switch">
                        Hide structure marks on script
                    </md-switch>
                </div>
                <button
                    v-if="canManageMarkers"
                    type="button"
                    class="ql-btn ql-btn--primary ql-btn--block bookmark-panel-btn bookmark-manage-btn"
                    @click="openManageMarkers">
                    Manage structure
                </button>
            </md-tab>
            <md-tab id="bookmarks" md-label="My Bookmarks" :md-active="currentTab === 'bookmarks'">
                <p v-if="!hasPrivateBookmarks" class="bookmark-empty-hint">No private bookmarks yet.</p>
                <md-list class="bookmarks-list" v-if="versionID && hasPrivateBookmarks">
                    <bookmark-item
                        v-for="(title, page) in versionBookmarks"
                        :key="page"
                        :currentPage="currentPage"
                        :page="page"
                        :title="title"
                        :startEdit="isFocusedBookmark(page)"
                        @gotoPage="gotoPage"
                        @editStarted="clearBookmarkFocus"
                    />
                </md-list>
                <md-list class="bookmarks-list" v-else-if="hasPrivateBookmarks">
                    <bookmark-item
                        v-for="(title, page) in bookmarks"
                        :key="page"
                        :currentPage="currentPage"
                        :page="page"
                        :title="title"
                        :startEdit="isFocusedBookmark(page)"
                        @gotoPage="gotoPage"
                        @editStarted="clearBookmarkFocus"
                    />
                </md-list>
                <button
                    type="button"
                    class="ql-btn ql-btn--primary ql-btn--block bookmark-panel-btn bookmark-import-btn"
                    :disabled="collaboratorList.length === 0"
                    :title="collaboratorList.length === 0
                        ? 'Bookmark import is only available from project collaborators'
                        : null"
                    @click="openImportBookmarkModal">
                    Import Bookmarks
                </button>
            </md-tab>
        </md-tabs>
        <manage-script-markers-dialog
            ref="manageMarkersDialog"
            :currentPage="currentPage"
            :pageWidth="0"
            :pageHeight="0"
            :totalPages="totalPages"
        />
        <md-dialog ref="import-bookmark-from">
            <md-dialog-title>Import Bookmarks</md-dialog-title>
            <md-dialog-content>
                <div v-if="collaboratorList.length === 0">
                    <span>Bookmark import is only available from project collaborators. You currently do not have any collaborators on this project.</span>
                </div>
                <template v-else>
                    <div v-for="collaborator in collaboratorList" :key="collaborator.uid">
                        <md-radio
                            v-if="collaborator.uid && collaborator.name"
                            v-model="candidate"
                            :md-value="collaborator.uid">
                            {{ collaborator.name }}
                        </md-radio>
                    </div>
                </template>
            </md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="cancelBookmarkImport">Cancel</md-button>
                <md-button class="md-primary" :disabled="!candidate" @click="importBookmarkFrom">Ok</md-button>
            </md-dialog-actions>
        </md-dialog>
        <div
            class="bookmarkNav-resizer"
            title="Drag to resize"
            @mousedown.prevent="startResize"
            @touchstart.prevent="startResize"
            @dblclick="resetWidth"
        ></div>
        <md-dialog-confirm
            :md-title="mergeBookmarkConfirm.title"
            :md-content-html="mergeBookmarkConfirm.description"
            :md-ok-text="mergeBookmarkConfirm.ok"
            :md-cancel-text="mergeBookmarkConfirm.cancel"
            @close="confirmMergeBookmark"
            ref="confirm-bookmark-merge"
        />
    </md-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import MarkerNavItem from '@/components/project/bookmark/marker-nav-item';
import BookmarkItem from '@/components/project/bookmark/bookmark';
import ManageScriptMarkersDialog from '@/components/project/markers/manage-markers-dialog';

// Tab order in the template; md-tabs identifies the active tab by index.
const TAB_IDS = ['structure', 'bookmarks'];

// Panel width. DEFAULT matches the stylesheet so nothing moves until dragged.
const NAV_WIDTH_KEY = 'bookmark-nav-width';
const NAV_WIDTH_DEFAULT = 260;
const NAV_WIDTH_MIN = 200;
// Never let the panel eat the whole viewport on a narrow window.
const NAV_WIDTH_MAX_FRACTION = 0.6;
const NAV_WIDTH_MAX_ABSOLUTE = 640;

function maxNavWidth() {
    const viewport = (typeof window !== 'undefined' && window.innerWidth) || 1280;
    return Math.max(NAV_WIDTH_MIN, Math.min(NAV_WIDTH_MAX_ABSOLUTE, viewport * NAV_WIDTH_MAX_FRACTION));
}

function clampNavWidth(value) {
    const width = Number(value);
    if (!Number.isFinite(width)) {
        return NAV_WIDTH_DEFAULT;
    }
    return Math.round(Math.min(maxNavWidth(), Math.max(NAV_WIDTH_MIN, width)));
}

const ProjectBookmarkNav = {
    name: 'ProjectBookmarkNav',
    props: {
        bookmarkNavOpened: { type: Boolean, default: false },
        currentPage: { type: Number, default: 1 },
        activeBookmarkTab: { type: String, default: 'structure' },
        // Page number of a private bookmark that was just added; its row opens
        // straight into title editing so the user can type without a dblclick.
        focusBookmarkPage: { type: [Number, String], default: null }
    },
    data() {
        return {
            mergeBookmarkConfirm: {
                title: 'Merge Bookmarks?',
                description: 'Warning, when importing bookmarks, all bookmarks on existing pages will be overwritten with the imported labels. You cannot undo this change.',
                ok: 'OK',
                cancel: 'CANCEL'
            },
            candidate: null,
            ownerData: null,
            // Local mirror of the hideStructureMarks preference. Flipped
            // optimistically so the switch feels instant on theatre wifi;
            // reconciled from the getter by the watcher below.
            hideMarksLocal: false,
            navWidth: NAV_WIDTH_DEFAULT,
            resizing: false,
            resizeStartX: 0,
            resizeStartWidth: 0
        };
    },
    computed: {
        ...mapGetters([
            'scriptMarkersNav',
            'bookmarks',
            'collaborators',
            'user',
            'project',
            'userByUID',
            'currentVersion',
            'canEditStructure',
            'hideStructureMarks'
        ]),
        currentTab() {
            return this.activeBookmarkTab || 'structure';
        },
        // Inline so it beats the stylesheet's fixed width; maxWidth is set too
        // because .bookmarkNav carries a 300px max-width that would clamp growth.
        navStyle() {
            return {
                width: `${this.navWidth}px`,
                maxWidth: `${this.navWidth}px`,
                flex: `0 0 ${this.navWidth}px`
            };
        },
        navMarkers() {
            return this.scriptMarkersNav || [];
        },
        canManageMarkers() {
            return this.canEditStructure;
        },
        totalPages() {
            return (this.project && Array.isArray(this.project.pdfPageNumbers))
                ? this.project.pdfPageNumbers.length
                : 0;
        },
        collaboratorList() {
            if (!this.owner) {
                return (this.collaborators || []).filter(collaborator => collaborator.uid !== this.user.uid);
            }
            return [...this.collaborators, {
                uid: this.owner.uid,
                name: this.owner.username
            }].filter(collaborator => collaborator.uid !== this.user.uid);
        },
        owner() {
            return this.ownerData;
        },
        versionID() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        versionBookmarks() {
            return this.currentVersion ? this.currentVersion.bookmarks : {};
        },
        hasPrivateBookmarks() {
            const source = this.versionID ? this.versionBookmarks : this.bookmarks;
            return source && typeof source === 'object' && Object.keys(source).length > 0;
        }
    },
    methods: {
        /*
         * md-tabs reports the active tab by INDEX, and fires for programmatic
         * activation as well as user clicks. Reporting it back up is safe — the
         * parent lands on the same value it already holds, so it settles.
         *
         * Note md-tabs has no `value` prop and no `model` option, so `v-model`
         * on it is silently inert; `:md-active` per tab is what actually works.
         */
        onTabChange(index) {
            this.$emit('setBookmarkTab', TAB_IDS[index] || TAB_IDS[0]);
        },
        /* ---- panel resizing ---------------------------------------------- */
        pointerX(e) {
            if (e.touches && e.touches[0]) {
                return e.touches[0].clientX;
            }
            return e.clientX || 0;
        },
        startResize(e) {
            this.resizing = true;
            this.resizeStartX = this.pointerX(e);
            this.resizeStartWidth = this.navWidth;
            window.addEventListener('mousemove', this.onResize);
            window.addEventListener('mouseup', this.stopResize);
            window.addEventListener('touchmove', this.onResize, { passive: false });
            window.addEventListener('touchend', this.stopResize);
            window.addEventListener('touchcancel', this.stopResize);
        },
        onResize(e) {
            if (!this.resizing) {
                return;
            }
            if (e.cancelable) {
                e.preventDefault();
            }
            // Panel is anchored left, so width tracks pointer movement directly.
            this.navWidth = clampNavWidth(
                this.resizeStartWidth + (this.pointerX(e) - this.resizeStartX)
            );
            this.syncTabLayout();
        },
        /*
         * md-tabs lays its panes out in JS: each pane gets width = $el.offsetWidth
         * and left = width * index, and the wrapper is translated by
         * width * activeTabNumber. It only recalculates on a window resize or via
         * a MutationObserver watching its own content subtree — dragging this
         * panel changes an inline style on an ANCESTOR, so neither fires and the
         * panes keep their old geometry, which is why both tabs became visible
         * side by side. calculateOnResize() is the same entry point vue-material
         * uses for window resizes; it also suppresses the slide animation, so
         * dragging doesn't look like tab switching.
         */
        syncTabLayout() {
            this.$nextTick(() => {
                const tabs = this.$refs.navTabs;
                if (tabs && typeof tabs.calculateOnResize === 'function') {
                    tabs.calculateOnResize();
                }
            });
        },
        stopResize() {
            if (!this.resizing) {
                return;
            }
            this.resizing = false;
            this.teardownResizeListeners();
            this.persistWidth();
            this.syncTabLayout();
        },
        resetWidth() {
            this.navWidth = NAV_WIDTH_DEFAULT;
            this.persistWidth();
            this.syncTabLayout();
        },
        persistWidth() {
            try {
                localStorage.setItem(NAV_WIDTH_KEY, String(this.navWidth));
            } catch (err) {
                // Private browsing / quota — the width just won't persist.
            }
        },
        restoreWidth() {
            let stored = null;
            try {
                stored = localStorage.getItem(NAV_WIDTH_KEY);
            } catch (err) {
                stored = null;
            }
            this.navWidth = stored === null ? NAV_WIDTH_DEFAULT : clampNavWidth(stored);
        },
        onWindowResize() {
            // Re-clamp: a stored width valid on a wide monitor can be too wide
            // after moving to a smaller screen.
            this.navWidth = clampNavWidth(this.navWidth);
            this.syncTabLayout();
        },
        teardownResizeListeners() {
            window.removeEventListener('mousemove', this.onResize);
            window.removeEventListener('mouseup', this.stopResize);
            window.removeEventListener('touchmove', this.onResize);
            window.removeEventListener('touchend', this.stopResize);
            window.removeEventListener('touchcancel', this.stopResize);
        },
        isFocusedBookmark(page) {
            return this.focusBookmarkPage != null
                && Number(page) === Number(this.focusBookmarkPage);
        },
        toggleBookmarkNav() {
            this.$emit('toggleBookmarkNav');
        },
        clearBookmarkFocus() {
            this.$emit('clearBookmarkFocus');
        },
        gotoPage(page) {
            this.$emit('gotoPage', page);
        },
        openManageMarkers() {
            this.$refs.manageMarkersDialog.open();
        },
        openImportBookmarkModal() {
            this.$refs['import-bookmark-from'].open();
        },
        cancelBookmarkImport() {
            this.$refs['import-bookmark-from'].close();
            this.candidate = null;
        },
        importBookmarkFrom() {
            if (this.candidate) {
                this.$refs['import-bookmark-from'].close();
                this.$refs['confirm-bookmark-merge'].open();
            }
        },
        confirmMergeBookmark(type) {
            if (type === 'ok') {
                this.$store.dispatch('mergeBookmark', { from: this.candidate });
                const candidate = this.collaborators.find(c => c.uid === this.candidate);
                if (candidate) {
                    this.$toast.success(`Bookmark successfully merged with ${candidate.name}.`);
                }
            }
            this.candidate = null;
        },
        async fetchOwner() {
            if (!this.project || !this.project.ownerUID) {
                return;
            }
            const owner = await this.userByUID(this.project.ownerUID);
            this.ownerData = owner;
        }
    },
    watch: {
        // Firestore -> local. The write round-trips through the live activeUser
        // binding and re-fires the getter, which is also what makes this sync
        // across the user's other tabs and devices. Guarded so that echo does
        // not re-enter the dispatch below.
        hideStructureMarks: {
            immediate: true,
            handler(val) {
                if (val !== this.hideMarksLocal) {
                    this.hideMarksLocal = val;
                }
            }
        },
        // Local -> Firestore. Driven from a watcher rather than @change because
        // vue-material emits `change` BEFORE `input`, so v-model has not updated
        // when @change runs. Same reason layer.vue's auto-numbering switch
        // persists from a watch (commit 134d0b76).
        hideMarksLocal(val) {
            if (val === this.hideStructureMarks) {
                return;
            }
            const attempted = val;
            this.$store.dispatch('setHideStructureMarks', attempted).catch((e) => {
                console.error('Could not save structure visibility:', e);
                // Only revert if nothing newer has been chosen since; rapid
                // toggling is last-write-wins, not a stack of reverts.
                if (this.hideMarksLocal === attempted) {
                    this.hideMarksLocal = !attempted;
                }
                if (this.$toast) {
                    this.$toast.error('Could not update structure visibility. Please try again.');
                }
            });
        }
    },
    created() {
        this.fetchOwner();
        this.restoreWidth();
        window.addEventListener('resize', this.onWindowResize);
    },
    beforeDestroy() {
        this.teardownResizeListeners();
        window.removeEventListener('resize', this.onWindowResize);
    },
    components: {
        'marker-nav-item': MarkerNavItem,
        'bookmark-item': BookmarkItem,
        'manage-script-markers-dialog': ManageScriptMarkersDialog
    }
};
export default ProjectBookmarkNav;
</script>

<style lang="scss">
.bookmarkNav {
    cursor: default;
    max-width: 300px;
    flex: 0 0 300px;
    position: relative;
    z-index: 20;

    /* Drag strip on the right edge. Sits slightly proud of the panel so it is
       grabbable without stealing clicks from the list beneath it. */
    .bookmarkNav-resizer {
        position: absolute;
        top: 0;
        bottom: 0;
        right: -3px;
        width: 8px;
        cursor: col-resize;
        z-index: 25;
        background: transparent;
        transition: background 0.15s ease;

        &:hover {
            background: rgba(13, 39, 68, 0.18);
        }
    }

    &.bookmarkNav--resizing .bookmarkNav-resizer {
        background: rgba(13, 39, 68, 0.32);
    }

    /* Kill text selection and hover flicker across the page while dragging. */
    &.bookmarkNav--resizing {
        user-select: none;
    }
    .closeBookmark {
        position: absolute;
        top: 0;
        right: 0;
        transform: scale(0.6);
        z-index: 2;
    }
    .bookmarkTopBar {
        min-height: 48px;
        background-color: #f5f5f5 !important;
        border-bottom: 1px solid #e0e0e0;
        color: #2c3e50;

        h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: #2c3e50;
        }

        .md-icon {
            color: #666 !important;
        }
    }
    .bookmark-empty-hint {
        font-size: 13px;
        color: #666;
        padding: 8px 16px;
        margin: 0;
    }
    /* Both panel buttons are plain <button class="ql-btn ql-btn--primary"> —
       colour, radius and type come from styles/design-system.scss, so this
       panel only owns layout. First two conversions off vue-material. */
    /* Both tabs put their management action in the same place: a footer under
       the list. margin-top:auto pins it to the bottom of the tab's flex column
       so the position is identical whether the list is empty, short or long —
       and so neither list starts lower than the other. */
    /* The visibility row and the panel action sit together in the panel foot.
       .bookmark-panel-btn carries margin-top:auto to pin itself down; when the
       row is present it takes that job instead, so the two stay adjacent rather
       than splitting the free space between two auto margins. */
    .structure-visibility {
        margin-top: auto;
        flex: 0 0 auto;
        padding: 12px 16px 4px;
    }
    .structure-visibility + .bookmark-panel-btn {
        margin-top: 0;
    }
    .structure-visibility__switch {
        margin: 0;
        font-size: 13px;
    }
    .bookmark-panel-btn {
        width: calc(100% - 32px);
        margin: 12px 16px 16px;
        margin-top: auto;
        flex: 0 0 auto;
    }
    .bookmark-nav-tabs {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;

        /* Equal-width Structure / My Bookmarks tabs only — scoped to this panel */
        > .md-tabs-navigation {
            flex: 0 0 auto;
            width: 100%;
            background-color: #0d2744 !important;

            .md-tabs-navigation-container,
            .md-tabs-navigation-scroll-container {
                width: 100%;
            }

            .md-tab-header {
                flex: 1 1 0 !important;
                max-width: none !important;
                min-width: 0 !important;
                justify-content: center;
                text-align: center;
                padding-left: 8px;
                padding-right: 8px;

                .md-tab-header-container {
                    justify-content: center;
                    width: 100%;
                }
            }

            /* Teal, not near-white. The bar is navy but the content directly
               below it is white, so a white indicator merged into the content
               and made the bar look mis-sized. Teal separates from both, and
               keys off the shared --accent token. Dark mode reads the same,
               where the old white line only looked right by accident. */
            .md-tab-indicator {
                background-color: var(--accent) !important;
                height: 3px;
            }
        }

        .md-tabs-content {
            flex: 1;
            min-height: 0;
            overflow: hidden;
        }

        .md-tab {
            padding: 0;
            height: 100%;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        }
    }
}
.md-theme-default .bookmarkNav, .md-theme-show-mode .bookmarkNav {
    background: #fff;
    position: fixed;
    top: 88px;
    left: 0;
    bottom: 0;
    z-index: 20;
    width: 260px;
    box-shadow: 12px 12px 14px 0 rgba(0, 0, 0,0.1);
    flex-wrap: nowrap;
}

.md-theme-show-mode.md-tabs>.md-tabs-navigation .md-tab-indicator {
    background-color: var(--accent) !important;
}

.md-theme-show-mode .bookmarkNav .bookmarkNav-resizer:hover,
.md-theme-show-mode .bookmarkNav.bookmarkNav--resizing .bookmarkNav-resizer {
    background: rgba(255, 255, 255, 0.28);
}

/* Dark mode for the panel buttons is handled by the design-system tokens
   (.md-theme-show-mode remaps --btn-primary-* to teal), so there is nothing
   panel-specific to declare here any more. */
@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait) {
    .md-theme-default .bookmarkNav, .md-theme-show-mode .bookmarkNav {
        top: 175px;
    }
}
@media only screen and (min-device-width: 768px) and (max-device-width: 1366px) and (orientation: landscape) {
    .md-theme-default .bookmarkNav, .md-theme-show-mode .bookmarkNav {
        top: 90px;
    }
}
.md-theme-show-mode .bookmarkNav {
    background: #424242 !important;

    .bookmarkTopBar {
        background-color: #333 !important;
        border-bottom-color: #555;
        color: #fff;

        h3 {
            color: #fff;
        }

        .md-icon {
            color: #fff !important;
        }
    }

    .bookmark-empty-hint {
        color: rgba(255, 255, 255, 0.7);
    }

    /* No button rule here on purpose: both panel buttons are .ql-btn and take
       their dark treatment from the design-system tokens. The old #555 override
       lived here, beat the token on specificity + !important, and applied to
       Manage structure only — so the two buttons didn't even match each other. */

    .bookmark-nav-tabs {
        > .md-tabs-navigation {
            background-color: #333 !important;

            .md-tab-header {
                color: rgba(255, 255, 255, 0.7) !important;

                &.md-active {
                    color: #fff !important;

                    .md-tab-header-container {
                        color: #fff !important;
                    }
                }
            }
        }
    }
}
.bookmarks-list.marker-nav-list,
.bookmarks-list {
    max-height: calc(100vh - 260px);
    overflow: auto;
}
.bookmarks-list.marker-nav-list,
.bookmarks-list {
    max-height: calc(100vh - 260px);
    overflow: auto;
}
</style>



// WEBPACK FOOTER //
// nav.vue?22e10101